import { apiClient } from '../utils/api-client';
import type {
  ClimbAssignment,
  RouteSettingBoardCard,
  RouteSettingBoardData,
  RouteSettingBoardResponse,
  RouteSettingBoardType,
  RouteSettingClimbResponse,
} from '../types/route-setting-board';

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  throw new Error(body.errors?.[0]?.detail || fallback);
}

class RouteSettingBoardService {
  /** The whole board for one climb type: every card plus the gym's zones. */
  async list(gymSlug: string, type: RouteSettingBoardType): Promise<RouteSettingBoardData> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/route_setting_climbs?type=${type}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to load the route-setting board');
    }

    const data: RouteSettingBoardResponse = await response.json();
    return {
      climbs: data.data.map((item) => ({ id: item.id, ...item.attributes })),
      zones: data.meta?.zones ?? [],
    };
  }

  /**
   * Moves a card to another column. Which attribute the column stands for
   * depends on how the board is grouped, and only the one sent is touched; null
   * clears it, which is the drop onto the "no zone" / "no setter" column.
   */
  async assign(
    gymSlug: string,
    climbId: string,
    attributes: ClimbAssignment
  ): Promise<RouteSettingBoardCard> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/route_setting_climbs/${climbId}`,
      { data: { attributes } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to move the climb');
    }

    const data: RouteSettingClimbResponse = await response.json();
    return { id: data.data.id, ...data.data.attributes };
  }

  /** The common case, kept named for what it does at the call site. */
  moveToZone(
    gymSlug: string,
    climbId: string,
    zoneId: string | null
  ): Promise<RouteSettingBoardCard> {
    return this.assign(gymSlug, climbId, { zone_id: zoneId });
  }

  /** Makes a member-reported climb official, and returns it as saved. */
  async confirm(gymSlug: string, climbId: string): Promise<RouteSettingBoardCard> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/route_setting_climbs/${climbId}/confirm`,
      {}
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to confirm the climb');
    }

    const data: RouteSettingClimbResponse = await response.json();
    return { id: data.data.id, ...data.data.attributes };
  }

  /** Takes a climb off the wall. Soft delete: its ascents survive. */
  async remove(gymSlug: string, climbId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/route_setting_climbs/${climbId}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the climb');
    }
  }
}

export const routeSettingBoardService = new RouteSettingBoardService();
